import { ChangeDetectionStrategy, Component, inject, input, output } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { map, of, switchMap } from "rxjs";

import { PremiumBadgeComponent } from "@vowkey/angular/billing/components/premium-badge";
import { AccountService } from "@vowkey/common/auth/abstractions/account.service";
import { BillingAccountProfileStateService } from "@vowkey/common/billing/abstractions";
import { SendType } from "@vowkey/common/tools/send/types/send-type";
import { PremiumUpgradePromptService } from "@vowkey/common/vault/abstractions/premium-upgrade-prompt.service";
import { ButtonModule, ButtonType, MenuModule } from "@vowkey/components";
import { I18nPipe } from "@vowkey/ui-common";

import { SendPolicyService } from "../services/send-policy.service";

// Desktop-specific version of NewSendDropdownComponent.
// Unlike the shared library version, this component emits events instead of using Angular Router,
// which aligns with Desktop's modal-based architecture.
@Component({
  selector: "tools-new-send-dropdown-v2",
  templateUrl: "new-send-dropdown-v2.component.html",
  imports: [I18nPipe, ButtonModule, MenuModule, PremiumBadgeComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewSendDropdownV2Component {
  readonly hideIcon = input<boolean>(false);
  readonly buttonType = input<ButtonType>("primary");

  readonly addSend = output<SendType>();

  protected readonly sendType = SendType;

  private readonly billingAccountProfileStateService = inject(BillingAccountProfileStateService);
  private readonly accountService = inject(AccountService);
  private readonly sendPolicyService = inject(SendPolicyService);
  private readonly premiumUpgradePromptService = inject(PremiumUpgradePromptService);

  protected readonly allowedSendTypes = toSignal(this.sendPolicyService.allowedSendTypes$, {
    initialValue: [SendType.Text, SendType.File],
  });

  protected readonly hasNoPremium = toSignal(
    this.accountService.activeAccount$.pipe(
      switchMap((account) => {
        if (!account) {
          return of(true);
        }
        return this.billingAccountProfileStateService
          .hasPremiumFromAnySource$(account.id)
          .pipe(map((hasPremium) => !hasPremium));
      }),
    ),
    { initialValue: true },
  );

  protected onTextSendClick(): void {
    this.addSend.emit(SendType.Text);
  }

  protected async onFileSendClick(): Promise<void> {
    if (this.hasNoPremium()) {
      await this.premiumUpgradePromptService.promptForPremium();
    } else {
      this.addSend.emit(SendType.File);
    }
  }

  /** Called when the type is restricted — directly creates the allowed type. */
  protected async onRestrictedClick(): Promise<void> {
    const allowedSendTypes = this.allowedSendTypes();
    if (allowedSendTypes[0] === SendType.File) {
      await this.onFileSendClick();
    } else if (allowedSendTypes[0] === SendType.Text) {
      this.onTextSendClick();
    }
  }
}
